/**
 * EL SITIO DE UN PARTE YA GUARDADO, para proponerle foco al confirmarlo.
 *
 * Un parte por id, abierto desde una lista que ya viene recortada: sin barrido
 * de alcance, igual que su ficha. Las columnas de focos son nuevas; si la base
 * no las tuviera la consulta falla y `failed` manda a la pantalla por el camino
 * de siempre, sin focos — confirmar no se rompe por esto.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { parseLegacyLine } from "@/lib/health/farmHealthModel";
import type { FollowUpAnswer } from "@/lib/health/focusLinking";
import type { SamplingPlace } from "@/hooks/useFocusLinkProposal";
import type { ConfirmSeverity, ConfirmVerdict } from "@/hooks/useConfirmScoutingSession";
import { readVoiceGuess } from "@/lib/pests/voicePlacement";

const SEVERITIES = ["low", "medium", "high", "critical"];
const VERDICTS = ["better", "same", "worse"];
const ANSWERS = ["same", "different", "unknown"];

export interface SessionPlace {
  place: SamplingPlace;
  severity: ConfirmSeverity | null;
  aiSeverity: ConfirmSeverity | null;
  verdict: ConfirmVerdict | null;
  /** El trabajador contestó «ya no hay»: lo que toca es una revisión limpia, no confirmar plaga. */
  workerSaysGone: boolean;
  /** Quién hizo el parte: si no es quien confirma, «cómo está» lo dijo él, no el confirmador (D19). */
  inspectorId: string | null;
  /** Lo que contestó a «¿cómo está?», tal cual (incluido «ya no hay»), o null si no contestó. */
  workerVerdict: "better" | "same" | "worse" | "gone" | null;
  /** Por qué NO se le pudo preguntar (D20), si fue el caso. */
  skipReason: "timeout" | "error" | "offline" | null;
  /** Contestó sobre un parte aún SIN confirmar de la misma línea (no había foco): solo información. */
  pendingHint: { sessionId: string; answer: FollowUpAnswer | null } | null;
  /** EL SITIO sobre el que contestó («¿es lo mismo?»): su número, su plaga y su línea, para que quien
      confirma sepa de qué habla el trabajador y la plaga venga ya elegida si dijo que sí. */
  hintFocus: { id: string; focusNumber: number; pestId: string | null; pestName: string; lineNumber: number | null } | null;
  /** La plaga que la IA entendió de la nota de voz del trabajador (D28): sugerencia, se puede cambiar. */
  voiceGuess: { pestId: string; pestName: string | null } | null;
}

export function useSessionPlace(sessionId: string | null, enabled: boolean) {
  const query = useQuery({
    queryKey: ["focus-link-candidates", "place", sessionId],
    enabled: enabled && !!sessionId,
    staleTime: 0,
    retry: false,
    queryFn: async (): Promise<SessionPlace | null> => {
      const { data, error } = await supabase
        .from("pest_scouting_sessions")
        .select("id, block_id, block_row_id, date, notes, anchor_t, anchor_source, gps_accuracy_m, severity, ai_analysis, evolution_verdict, followup_focus_id, followup_answer, followup_task_id, followup_skip_reason, followup_session_id, inspector_id, voice_guess")
        .eq("id", sessionId!)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const ai = (data.ai_analysis ?? null) as { severity?: string; identified?: boolean; outcome?: string } | null;
      /* La gravedad de la IA solo se sugiere si lo que vio es una PLAGA (o un análisis sin
         `outcome`, el de siempre). Un auxiliar, una carencia o «no se puede saber» no
         describen la gravedad de ninguna plaga (05-10-2026, plan cultivos SIEX F7). */
      const aiEsPlaga = !ai?.outcome || ai.outcome === "pest";
      const answer = data.followup_answer && ANSWERS.includes(data.followup_answer) ? (data.followup_answer as FollowUpAnswer) : null;
      const hinted = data.followup_focus_id != null || data.followup_task_id != null;
      // De qué sitio habla el trabajador: no fatal, sin esto solo se pierde la preselección.
      let hintFocus: SessionPlace["hintFocus"] = null;
      if (data.followup_focus_id) {
        const { data: f } = await supabase
          .from("pest_foci")
          .select("id, focus_number, pest_id, pest_name_snapshot, line_number")
          .eq("id", data.followup_focus_id)
          .maybeSingle();
        if (f) hintFocus = { id: f.id, focusNumber: f.focus_number, pestId: f.pest_id, pestName: f.pest_name_snapshot, lineNumber: f.line_number };
      }
      return {
        place: {
          sessionId: data.id,
          blockId: data.block_id,
          rowId: data.block_row_id,
          lineNumber: parseLegacyLine(data.notes),
          anchorT: data.anchor_t,
          anchorSource: data.anchor_source,
          accuracyM: data.gps_accuracy_m,
          date: data.date,
          hint: hinted ? { focusId: data.followup_focus_id, answer, fromTask: data.followup_task_id != null } : null,
        },
        severity: data.severity && SEVERITIES.includes(data.severity) ? (data.severity as ConfirmSeverity) : null,
        aiSeverity: aiEsPlaga && ai?.identified !== false && ai?.severity && SEVERITIES.includes(ai.severity) ? (ai.severity as ConfirmSeverity) : null,
        verdict: data.evolution_verdict && VERDICTS.includes(data.evolution_verdict) ? (data.evolution_verdict as ConfirmVerdict) : null,
        workerSaysGone: data.evolution_verdict === "gone",
        inspectorId: data.inspector_id ?? null,
        workerVerdict: data.evolution_verdict && ["better", "same", "worse", "gone"].includes(data.evolution_verdict) ? (data.evolution_verdict as "better" | "same" | "worse" | "gone") : null,
        skipReason: data.followup_skip_reason && ["timeout", "error", "offline"].includes(data.followup_skip_reason) ? (data.followup_skip_reason as "timeout" | "error" | "offline") : null,
        pendingHint: !hinted && data.followup_session_id ? { sessionId: data.followup_session_id as string, answer } : null,
        hintFocus,
        voiceGuess: readVoiceGuess(data.voice_guess),
      };
    },
  });
  return { data: query.data ?? null, loading: query.isLoading, failed: !!query.error };
}
